import { useId, useState } from 'react'
import { V3_SECONDARY_BUTTON_CLS } from './controlStyles'
import { isStatusTone, tonePillClasses, type StatusTone } from './statusTone'

export interface MessageListDefinition {
    rowsField: string
    messageField: string
    title: string
    tone?: StatusTone
    emptyText?: string
    previousLabel?: string
    nextLabel?: string
    idField?: string
    selectedIdField?: string
    actionLabelField?: string
}

/** Read-only messages: initially collapsed, 25 per page, never silently truncated. */
export function MessageList({ definition: d, record, onSelect }: {
    definition: MessageListDefinition
    record: Record<string, unknown>
    onSelect?: ((id: string) => void) | undefined
}) {
    const [open, setOpen] = useState(false)
    const [page, setPage] = useState(0)
    const id = useId()
    const raw = record[d.rowsField]
    const selectionKeys = [d.idField, d.selectedIdField, d.actionLabelField]
    if (selectionKeys.some(key => key !== undefined) && selectionKeys.some(key => typeof key !== 'string' || !key.trim())) {
        return <p role="alert">Message selection requires idField, selectedIdField and actionLabelField.</p>
    }
    if (!Array.isArray(raw) || raw.length > 10_000 || raw.some(row =>
        !row || typeof row !== 'object' || Array.isArray(row) || typeof row[d.messageField] !== 'string')) {
        return <p role="alert">Messages are invalid or exceed the 10,000-row limit. Narrow or refresh the source.</p>
    }
    if (d.idField && (raw.some(row => typeof row[d.idField!] !== 'string' || !row[d.idField!].trim()) ||
        new Set(raw.map(row => row[d.idField!])).size !== raw.length)) {
        return <p role="alert">Message identities are missing or duplicated. Refresh the source.</p>
    }
    const currentPage = Math.min(page, Math.max(0, Math.ceil(raw.length / 25) - 1))
    const tone = isStatusTone(d.tone) ? d.tone : 'neutral'
    return <section className={`rounded-xl p-4 ${tonePillClasses[tone]}`}>
        <button type="button" className="text-left font-medium rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>{raw.length} {d.title}</button>
        <div id={id} hidden={!open}>
            {open && <>
                {raw.length === 0 ? <p className="mt-3 text-sm">{d.emptyText ?? 'No messages.'}</p>
                    : <ul className="mt-3 space-y-2 text-sm">{raw.slice(currentPage * 25, (currentPage + 1) * 25).map((row, index) =>
                        <li key={d.idField ? row[d.idField] : `${currentPage}/${index}`}>
                            {row[d.messageField]}
                            {onSelect && d.idField && d.actionLabelField && typeof row[d.actionLabelField] === 'string' && row[d.actionLabelField].trim() &&
                                <button type="button" className={`${V3_SECONDARY_BUTTON_CLS} ml-3`}
                                    onClick={() => onSelect(row[d.idField!])}>{row[d.actionLabelField]}</button>}
                        </li>)}</ul>}
                {raw.length > 25 && <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                    <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={currentPage === 0}
                        onClick={() => setPage(currentPage - 1)}>{d.previousLabel ?? 'Previous messages'}</button>
                    <span aria-live="polite">{currentPage * 25 + 1}–{Math.min((currentPage + 1) * 25, raw.length)} of {raw.length}</span>
                    <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={(currentPage + 1) * 25 >= raw.length}
                        onClick={() => setPage(currentPage + 1)}>{d.nextLabel ?? 'Next messages'}</button>
                </div>}
            </>}
        </div>
    </section>
}
